import React from 'react'
import { Grid, Typography, generateSrcSet, imageCrossOrigin, makeStyles, retryCorsImageOnError } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import type { QuoteFile } from 'paintscout'
import { ImgComparisonSlider } from '@img-comparison-slider/react'
import PhotoLibraryOutlinedIcon from '@material-ui/icons/PhotoLibraryOutlined'
import type { PresentationBlockProps } from '../..'
import compareBlockProps from '../../util/compareBlockProps'
import { getObjectPosition } from '../../inlineEditable'
import { getSliderAspectRatio } from './getSliderAspectRatio'
import { getSliderPointerEventsDisabled } from './getSliderPointerEvents'

// QuoteFile.height is optional — used when either image is missing dimension data so the
// slider doesn't collapse to a zero/NaN height. Only used as a fallback when neither image has
// an explicit `presentationOptions.aspectRatio` set (see getSliderAspectRatio). Also the fixed
// height of the empty-state placeholder, so there's no layout jump once both images are set.
const DEFAULT_SLIDER_HEIGHT = 400

const useStyles = makeStyles<Theme, { maxHeight: number; aspectRatio: string | null }>(
  (theme) => ({
    root: {},
    slider: ({ maxHeight, aspectRatio }) => ({
      justifyContent: 'center',
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden',
      ...(aspectRatio ? { aspectRatio, height: 'auto', maxHeight: 'none' } : { maxHeight, height: maxHeight }),
      '&:focus': {
        outline: 'none'
      }
    }),
    // In the editor, this block's own mousedown listener (bound directly on the custom
    // element, anywhere across its surface — not just the visible handle) wins the gesture
    // before it ever reaches the generic per-block click-to-select or drag-to-reorder handlers
    // in Presentation.tsx, so an unselected block can never be selected or reordered by
    // grabbing it (#24097 follow-up). Disabling pointer events here while unfocused makes the
    // browser skip hit-testing it entirely, so clicks/drags fall through to the normal block
    // chrome underneath instead. Applied only while unfocused (see getSliderPointerEventsDisabled)
    // — drag-to-reorder is already disabled for the focused block (dragBlocks.ts).
    // Kept as a STATIC rule on purpose: `slider` above is function-valued, so MUI injects it in a
    // dynamic sheet after the static one, and a `pointer-events` set there can't be overridden by a
    // static sibling class (#24185).
    sliderDisabled: {
      pointerEvents: 'none'
    },
    image: ({ maxHeight, aspectRatio }) => ({
      width: '100%',
      maxWidth: '100%',
      objectFit: 'cover',
      ...(aspectRatio ? { aspectRatio, height: '100%', maxHeight: 'none' } : { maxHeight, height: maxHeight })
    }),
    svg: { margin: '-20px' },
    placeholder: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing(1),
      height: DEFAULT_SLIDER_HEIGHT,
      width: '100%',
      color: theme.palette.text.secondary,
      border: `1px dashed ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius
    },
    placeholderIcon: {
      fontSize: 40
    }
  }),
  { name: 'SliderImageBlock' }
)

function SliderImageBlock(props: PresentationBlockProps) {
  const { isEditable, isSettings, isPreview, block, WrapperComponent } = props
  const { fields } = block
  const { leftImage, rightImage } = fields ?? {}
  // both images are always replaced from this block's Options panel (SliderImageOptions.tsx) —
  // mirrors Banner's poster-image "Replace Image" precedent — so the canvas itself is read-only
  const classes = useStyles({ maxHeight: DEFAULT_SLIDER_HEIGHT, aspectRatio: null })

  return (
    <WrapperComponent {...props}>
      <Grid container spacing={3} style={{ justifyContent: 'center' }}>
        <Grid item md={12} sm={12} xs={12}>
          {leftImage?.src && rightImage?.src ? (
            <SliderImageContainer
              leftImage={leftImage}
              rightImage={rightImage}
              pointerEventsDisabled={getSliderPointerEventsDisabled({
                isSettings: !!isSettings,
                isPreview: !!isPreview,
                isFocused: !!isEditable
              })}
            />
          ) : (
            <div className={classes.placeholder}>
              <PhotoLibraryOutlinedIcon className={classes.placeholderIcon} />
              <Typography variant={'body2'}>Add Before/After images from the panel</Typography>
            </div>
          )}
        </Grid>
      </Grid>
    </WrapperComponent>
  )
}

// clears a resized (Lambda-not-ready-yet or, for #24097, permanently-malformed) srcset URL so the
// browser retries with the plain `src`, mirroring ResponsiveImage.tsx's onError — this component
// builds its own <img> tags rather than using ResponsiveImage, so it needs its own copy of the
// fallback. Module-level (not a hook closure) since it captures no props/state, so a memoized
// SliderImageContainer doesn't need a fresh instance every render.
function handleImageError(e: React.SyntheticEvent<HTMLImageElement>) {
  if (e.currentTarget.srcset) {
    e.currentTarget.srcset = ''
    return
  }
  retryCorsImageOnError(e)
}

const SliderImageContainer = React.memo(function SliderImageContainer({
  leftImage,
  rightImage,
  pointerEventsDisabled
}: {
  leftImage: QuoteFile
  rightImage: QuoteFile
  pointerEventsDisabled: boolean
}) {
  const generatedLeftSrc = generateSrcSet({ file: leftImage })
  const generatedRightSrc = generateSrcSet({ file: rightImage })
  const leftHeight = leftImage.height ?? rightImage.height ?? DEFAULT_SLIDER_HEIGHT
  const rightHeight = rightImage.height ?? leftImage.height ?? DEFAULT_SLIDER_HEIGHT
  const maxHeight = Math.min(leftHeight, rightHeight)
  const aspectRatio = getSliderAspectRatio({ leftImage, rightImage })
  const classes = useStyles({ maxHeight, aspectRatio })

  return (
    <ImgComparisonSlider
      className={pointerEventsDisabled ? `${classes.slider} ${classes.sliderDisabled}` : classes.slider}
    >
      <svg
        // @ts-ignore
        slot="handle"
        xmlns="http://www.w3.org/2000/svg"
        width="100"
        viewBox="-8 -3 16 6"
        className={classes.svg}
      >
        <path
          stroke="#fff"
          d="M -5 -2 L -7 0 L -5 2 M -5 -2 L -5 2 M 5 -2 L 7 0 L 5 2 M 5 -2 L 5 2"
          strokeWidth="1"
          fill="#fff"
          vectorEffect="non-scaling-stroke"
        ></path>
      </svg>
      <img
        slot="first"
        src={leftImage.src}
        alt={(leftImage as any).alt}
        srcSet={generatedLeftSrc}
        className={classes.image}
        style={aspectRatio ? { objectPosition: getObjectPosition(leftImage) } : undefined}
        crossOrigin={imageCrossOrigin(leftImage)}
        onError={handleImageError}
      />
      <img
        slot="second"
        src={rightImage.src}
        alt={(rightImage as any).alt}
        srcSet={generatedRightSrc}
        className={classes.image}
        style={aspectRatio ? { objectPosition: getObjectPosition(rightImage) } : undefined}
        crossOrigin={imageCrossOrigin(rightImage)}
        onError={handleImageError}
      />
    </ImgComparisonSlider>
  )
})

export default React.memo(SliderImageBlock, (prevProps, nextProps) => {
  return compareBlockProps(prevProps, nextProps)
})
